import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { LazyBoundary } from "../../components/LazyBoundary.jsx";
import { useSessionCtx } from "../../context/SessionContext";
import {
  SectionHeader,
  RadioGroup,
  EmptyState,
  Button,
  Icon,
} from "../../components/Elements";
import { MiniSwitch } from "../../components/MiniSwitch.jsx";
import { SettingRow } from "../../components/SettingRow.jsx";
import { FullViewModal, StatusChip, FullViewButton } from "../../components/FullViewModal.jsx";
import { usePollCard } from "./usePollCard.js";
import { isInteractiveQuiz } from "../../utils/pollTypes.js";
import { getClirnetSurveyOptions } from "../../survey/clirnetSurvey/index.js";
import { useUserPollActiveIds } from "../../hooks/useAdminActiveHistorySync.js";
import { MessageText } from "../../constants/constants.js";
import parse from "html-react-parser";
import DOMPurify from "dompurify";

// SurveyJS (survey-core + its React UI and CSS) is the largest dependency in the app and only
// shows here, in a SurveyJS poll's card: load it with the first one instead of at startup.
const SurveyJsRenderer = lazy(() => import("../../survey/surveyJs/SurveyJsRenderer.jsx"));

// Sanitize CMS HTML (same approach as DashboardPage) and drop the empty paragraphs the
// editor leaves behind (<p>&nbsp;</p>, <p><br></p>) so they don't add blank lines.
function cleanHtml(html) {
  return DOMPurify.sanitize(String(html ?? ""), { FORCE_BODY: true })
    .replace(/<p>(?:\s|&nbsp;|&#160;|<br\s*\/?>)*<\/p>/gi, "")
    .trim();
}

// Renders question/option HTML safely. Tailwind's reset strips list and link styles,
// so the basics are restored for content inside.
function RichText({ html, fallback = "", className = "", as: Tag = "div" }) {
  const clean = cleanHtml(html);
  return (
    <Tag
      className={`rich-text break-words [&_p]:m-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-blue-600 [&_a]:underline [&_img]:max-w-full [&_img]:h-auto ${className}`}
    >
      {clean ? parse(clean) : fallback}
    </Tag>
  );
}

// How long the admin's own click is trusted before the user-poll echo takes over.
const OPTIMISTIC_TTL_MS = 15000;

// embedded: rendered inside the MainPage accordion (hides this page's own SectionHeader).
// filter: "all" | "active" | "inactive" - applied by the ControlTabs radio filter.
export default function Poll({ embedded = false, filter = "all" } = {}) {
  const ctx = useSessionCtx();
  const activeIds = useUserPollActiveIds(ctx?.session?.sessionId);
  // Active state normally comes from the user-poll channel, so a slow (or down) socket
  // makes the switch look dead: remember the admin's own last click per id and show it
  // immediately, then let the channel win again as soon as it agrees or after the TTL.
  const [optimistic, setOptimistic] = useState({});

  // Drop an override as soon as the channel agrees with it (the echo arrived) ...
  useEffect(() => {
    setOptimistic((prev) => {
      const keys = Object.keys(prev);
      if (!keys.length) return prev;
      const keep = keys.filter((k) => prev[k].value !== activeIds.has(k));
      return keep.length === keys.length
        ? prev
        : Object.fromEntries(keep.map((k) => [k, prev[k]]));
    });
  }, [activeIds]);

  // ... and after the TTL regardless, so the channel always gets the last word even if
  // the echo never arrives (socket down).
  useEffect(() => {
    const entries = Object.entries(optimistic);
    if (!entries.length) return undefined;
    const delay = Math.max(
      0,
      Math.min(...entries.map(([, v]) => v.at + OPTIMISTIC_TTL_MS - Date.now())),
    );
    const timer = setTimeout(() => {
      setOptimistic((prev) => {
        const now = Date.now();
        const keep = Object.fromEntries(
          Object.entries(prev).filter(([, v]) => now - v.at < OPTIMISTIC_TTL_MS),
        );
        return Object.keys(keep).length === Object.keys(prev).length ? prev : keep;
      });
    }, delay);
    return () => clearTimeout(timer);
  }, [optimistic]);

  if (!ctx) return null;
  // Single source of truth for "is this an interactive quiz?" — the old inline
  // check disagreed with usePollControls/adminStore on conflicting type/category
  // markers, hiding polls that other lists kept (and vice versa).
  const rawPolls = (Array.isArray(ctx.polls) ? ctx.polls : []).filter(
    (p) => p && typeof p === "object" && !isInteractiveQuiz(p),
  );
  const polls = rawPolls.map((p) => {
    const own = optimistic[String(p.id)];
    return { ...p, isApproved: own ? own.value : activeIds.has(String(p.id)) };
  });
  const { pollToggle, pollType, pollStage, pollShowResult } = ctx;
  const handleToggle = (id, next) => {
    setOptimistic((prev) => ({ ...prev, [String(id)]: { value: next, at: Date.now() } }));
    pollToggle(id, next);
  };
  // Show Result on / off both leave the poll live (results shown, or back to voting): show
  // it as live at once too, so the switches don't wait for the channel.
  const handleShowResult = (id, next) => {
    setOptimistic((prev) => ({ ...prev, [String(id)]: { value: true, at: Date.now() } }));
    pollShowResult(id, next);
  };
  // Start / End on = the poll goes active, off = inactive: show that at once too.
  const handleStage = (id, stage) => {
    setOptimistic((prev) => ({ ...prev, [String(id)]: { value: stage != null, at: Date.now() } }));
    pollStage(id, stage);
  };
  // isApproved = what viewers can see (channel), reconciled with the admin's own click.
  const visible =
    filter === "all"
      ? polls
      : polls.filter((p) => (filter === "active" ? p.isApproved : !p.isApproved));

  return (
    <div className="w-full max-w-4xl">
      {!embedded && (
      <SectionHeader
        title="Polls Management"
        icon="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        description="Enable or disable polls and set them to In chat or Popup"
        badge={`${polls.filter((p) => p.isApproved).length}/${polls.length} active`}
      />
      )}
      {polls.length === 0 && (
        <EmptyState
          icon="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          title={MessageText.emptyPolls.title}
          description={MessageText.emptyPolls.description}
        />
      )}
      {polls.length > 0 && visible.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-6">
          No {filter} polls
        </p>
      )}
      <div className="space-y-3">
        {visible.map((p) => (
          <PollCard
            key={String(p.id)}
            poll={p}
            pollToggle={handleToggle}
            pollType={pollType}
            pollStage={handleStage}
            pollShowResult={handleShowResult}
          />
        ))}
      </div>
    </div>
  );
}

// Full poll in a modal: every question with its options (correct ones highlighted).
function PollFullView({ poll: p, onClose }) {
  const { isSurveyJs, isClirnet, surveys, titleHtml, options, points } = usePollCard(p, 0);
  // SurveyJS polls keep their schema on poll.question.
  const surveyJsSchema = p && typeof p.question === "object" && p.question !== null ? p.question : undefined;
  const typeLabel = isSurveyJs ? "SurveyJS" : typeof p.category === "string" && p.category ? p.category : "Poll";

  return (
    <FullViewModal
      labelId={`poll-full-${p.id}`}
      onClose={onClose}
      meta={
        <>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{typeLabel}</span>
          <StatusChip active={p.isApproved === true} />
          {points != null && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
              {points} {points === 1 ? "pt" : "pts"}
            </span>
          )}
        </>
      }
      title={<RichText html={p.title || titleHtml} fallback="Untitled poll" className="text-base font-semibold text-gray-900" />}
    >
      {isSurveyJs && surveyJsSchema ? (
        <LazyBoundary className="text-gray-500">
          <Suspense fallback={<p className="text-sm text-gray-400">Loading poll…</p>}>
            <SurveyJsRenderer schema={surveyJsSchema} mode="readonly" />
          </Suspense>
        </LazyBoundary>
      ) : isClirnet && surveys.length ? (
        surveys.map((q, i) => {
          const qOptions = getClirnetSurveyOptions(q);
          return (
            <div key={q?.questionID ?? i}>
              <div className="flex gap-2">
                <span className="text-xs font-semibold text-gray-400 shrink-0 mt-0.5">Q{i + 1}</span>
                <RichText html={q?.question} fallback="No question set" className="flex-1 min-w-0 text-sm font-semibold text-gray-900" />
              </div>
              {qOptions.length > 0 ? <PollOptionList list={qOptions} /> : <p className="mt-2 text-xs text-gray-400 italic">No options</p>}
            </div>
          );
        })
      ) : Array.isArray(options) && options.length ? (
        <PollOptionList list={options} />
      ) : (
        <p className="text-sm text-gray-400">No questions configured for this poll.</p>
      )}
    </FullViewModal>
  );
}

// One question's options (Clirnet data or legacy poll.options); correct ones highlighted.
function PollOptionList({ list }) {
  return (
    <div className="space-y-1.5 mt-2">
      {list.map((o, i) => {
        const correct = o.isCorrect === true || o.is_correct === true || o.isCorrect === 1 || o.is_correct === 1;
        return (
          <div
            key={o.id ?? i}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${correct ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-gray-50 border-gray-200 text-gray-800"}`}
          >
            <span className="w-5 h-5 rounded-md bg-white border border-gray-200 text-[10px] font-semibold text-gray-500 flex items-center justify-center shrink-0">
              {String.fromCharCode(65 + (i % 26))}
            </span>
            <RichText html={o.label ?? o.text} as="span" className="flex-1 min-w-0" />
            {correct && <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 shrink-0">Correct</span>}
          </div>
        );
      })}
    </div>
  );
}

// The poll card's settings tabs: the live controls, and the session start / end popup.
const POLL_CARD_TABS = [
  { key: "controls", title: "Controls" },
  { key: "stage", title: "Start / End" },
];

// Poll card in the admin theme (white rounded-2xl card, dark gradient icon badge, emerald = active):
//   header   - icon, title (max 2 lines), type tag + status chip; Poll details button
//   settings - two underlined tabs:
//     Controls    - Active + Show Result switches (one on at a time); Display (In chat / Popup -
//                   pollType "section" / "popup")
//     Start / End - Start poll and End poll switches, one row each (the session start / end
//                   popup); the tab shows which. Disabled while the poll is Active / Show Result.
// A session start / end poll is always an active popup without results: its Controls stay
// visible but disabled, and the card opens on Start / End.
function PollCard({ poll: p, pollToggle, pollType, pollStage, pollShowResult }) {
  const [fullOpen, setFullOpen] = useState(false);
  const closeFull = useCallback(() => setFullOpen(false), []);
  const { isSurveyJs, titleHtml } = usePollCard(p, 0);
  const idForActions = String(p.id);
  const resultsShown = p.isApproved && p.showresult === true;
  // Active and Show Result are one-at-a-time: Active = live for voting (isApproved, results
  // hidden), Show Result = live with results. Switching one on switches the other off
  // (pollToggle publishes showresult: false, pollShowResult showresult: true).
  const votingLive = p.isApproved === true && !resultsShown;
  const isStartPoll = p.sessionStartPoll === true;
  const isEndPoll = p.sessionEndPoll === true;
  const isStagePoll = isStartPoll || isEndPoll;
  const stageLabel = isStartPoll ? "Start" : isEndPoll ? "End" : null;
  // A regular poll that is live (Active / Show Result) cannot be made a start / end poll.
  // A start / end poll is always active itself, so its own switches stay usable.
  const stageLocked = !isStagePoll && (p.isApproved === true || resultsShown);
  const typeLabel = isSurveyJs ? "SurveyJS" : typeof p.category === "string" && p.category ? p.category : "Poll";

  const [tab, setTab] = useState(() => (isStagePoll ? "stage" : "controls"));
  const tabId = (key) => `poll-${idForActions}-tab-${key}`;
  const panelId = (key) => `poll-${idForActions}-panel-${key}`;
  const openTab = (key) => {
    setTab(key);
    document.getElementById(tabId(key))?.focus();
  };
  // Arrow keys / Home / End move between the tabs (one tab stop, like the session tabs).
  const onTabKeyDown = (e) => {
    const i = POLL_CARD_TABS.findIndex((t) => t.key === tab);
    let next = null;
    if (e.key === "ArrowRight") next = (i + 1) % POLL_CARD_TABS.length;
    else if (e.key === "ArrowLeft") next = (i - 1 + POLL_CARD_TABS.length) % POLL_CARD_TABS.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = POLL_CARD_TABS.length - 1;
    if (next == null) return;
    e.preventDefault();
    openTab(POLL_CARD_TABS[next].key);
  };

  return (
    <article
      className={`w-full max-w-xl bg-white rounded-2xl border overflow-hidden transition-shadow hover:shadow-sm ${p.isApproved ? "border-emerald-200" : "border-gray-200/70"}`}
    >
      {/* Header */}
      <header className="flex items-start gap-3 p-3.5">
        <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-gray-800 to-black text-white flex items-center justify-center shrink-0">
          <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 20V10 M12 20V4 M6 20V14" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <RichText
            html={p.title || titleHtml}
            fallback="Untitled poll"
            className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2"
          />
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{typeLabel}</span>
            {p.id != null && (
              <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 tabular-nums" title="Poll ID">
                ID {idForActions}
              </span>
            )}
            <StatusChip active={p.isApproved === true} />
            {resultsShown && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-900 text-white">Results visible</span>
            )}
          </div>
        </div>
        <FullViewButton label="Poll details" onClick={() => setFullOpen(true)} />
      </header>

      {/* Settings: plain tabs - the name, underlined while selected, over a bottom border */}
      <div className="border-t border-gray-100 bg-gray-50/60">
        <div role="tablist" aria-label="Poll settings" onKeyDown={onTabKeyDown} className="flex gap-5 border-b border-gray-200 px-3.5">
          {POLL_CARD_TABS.map(({ key, title }) => {
            const selected = key === tab;
            return (
              <button
                key={key}
                id={tabId(key)}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={panelId(key)}
                tabIndex={selected ? 0 : -1}
                onClick={() => setTab(key)}
                className={`-mb-px inline-flex items-center gap-1.5 border-b-2 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-gray-900 ${
                  selected ? "border-gray-900 text-gray-900" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800"
                }`}
              >
                {title}
                {key === "stage" && stageLabel && (
                  <span className="rounded-full bg-emerald-50 px-1.5 text-[10px] font-semibold text-emerald-700">{stageLabel}</span>
                )}
              </button>
            );
          })}
        </div>

        <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
          {/* A start / end poll is always an active popup without results: shown, but disabled. */}
          {tab === "controls" && (
            <div className="divide-y divide-gray-100">
              {/* Both switches side by side (each label + switch is one click target); only one
                  is on at a time - see votingLive. */}
              <div className="flex items-center justify-between gap-3 px-3.5 py-2">
                <MiniSwitch
                  checked={votingLive}
                  onChange={(v) => pollToggle(idForActions, v)}
                  label="Active"
                  hint={isStagePoll ? "Always on for a start / end poll" : "Show this poll to viewers for voting (turns Show Result off)"}
                  disabled={isStagePoll}
                />
                <MiniSwitch
                  checked={resultsShown}
                  onChange={(v) => pollShowResult(idForActions, v)}
                  label="Show Result"
                  hint={isStagePoll ? "Always off for a start / end poll" : "Show the results to viewers (turns Active off)"}
                  disabled={isStagePoll}
                />
              </div>
              <SettingRow label="Display">
                <RadioGroup
                  value={p.pollType || "section"}
                  onChange={(val) => pollType(idForActions, val)}
                  name={`pt_${idForActions}`}
                  horizontal
                  disabled={isStagePoll}
                  options={[
                    { value: "section", label: "In chat" },
                    { value: "popup", label: "Popup" },
                  ]}
                />
              </SettingRow>
            </div>
          )}

          {/* One row each; only one can be on (switching one on turns the other off). Start =
              popup when the live session opens, End = popup on the stream ended screen.
              A poll that is live (Active / Show Result) cannot become one until it is off. */}
          {tab === "stage" && (
            <div className="divide-y divide-gray-100">
              <SettingRow label="Start poll">
                <MiniSwitch
                  checked={isStartPoll}
                  onChange={(on) => pollStage(idForActions, on ? "start" : null)}
                  ariaLabel="Start poll"
                  hint={stageLocked ? "Turn off Active and Show Result first" : "Pops up for viewers when the live session opens"}
                  disabled={stageLocked}
                />
              </SettingRow>
              <SettingRow label="End poll">
                <MiniSwitch
                  checked={isEndPoll}
                  onChange={(on) => pollStage(idForActions, on ? "end" : null)}
                  ariaLabel="End poll"
                  hint={stageLocked ? "Turn off Active and Show Result first" : "Pops up for viewers when the stream ends"}
                  disabled={stageLocked}
                />
              </SettingRow>
            </div>
          )}
        </div>
      </div>

      {fullOpen && <PollFullView poll={p} onClose={closeFull} />}
    </article>
  );
}
